import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { WHATSAPP_COMMUNITY_URL } from "@/constants/social";

export default function CommunitySection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const socials = [
    { icon: "ri-whatsapp-line", label: "WhatsApp", color: "#25D366", href: WHATSAPP_COMMUNITY_URL },
    { icon: "ri-linkedin-line", label: "LinkedIn", color: "#0A66C2", href: "#" },
    { icon: "ri-twitter-x-line", label: "Twitter/X", color: "#1A1A1A", href: "#" },
    { icon: "ri-facebook-line", label: "Facebook", color: "#1877F2", href: "#" },
  ];

  return (
    <section ref={ref} className="py-24 bg-[#1A2A28] text-white overflow-hidden relative">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#E8521A]/5 blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-[#1A8C7A]/10 blur-3xl pointer-events-none"></div>

      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#E8521A]/20 text-[#FF8C5A] text-sm font-medium mb-4">
            Espace Communauté
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Rejoignez la Communauté</h2>
          <p className="text-lg text-gray-400 max-w-xl mx-auto">
            Connectez-vous avec vos collègues, partagez vos performances et restez informé des activités de l'ASC
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {[
            {
              icon: "ri-team-line",
              title: "Squads",
              desc: "Rejoignez ou créez votre groupe de sport",
              link: "/communaute/squads",
              count: "48 squads actifs",
            },
            {
              icon: "ri-discuss-line",
              title: "Forum",
              desc: "Échangez, partagez et commentez",
              link: "/communaute/forum",
              count: "1 240 publications",
            },
            {
              icon: "ri-notification-3-line",
              title: "Notifications",
              desc: "Rappels d'événements et actualités",
              link: "/espace-membre",
              count: "Temps réel",
            },
          ].map((item, i) => (
            <Link
              key={i}
              to={item.link}
              className={`group p-8 rounded-3xl border-2 border-white/15 hover:border-[#E8521A]/50 hover:bg-white/5 transition-all duration-300 cursor-pointer ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#E8521A]/15 mb-6 group-hover:bg-[#E8521A]/25 transition-colors">
                <i className={`${item.icon} text-2xl text-[#E8521A]`}></i>
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-[#E8521A] transition-colors">{item.title}</h3>
              <p className="text-gray-400 text-sm mb-3">{item.desc}</p>
              <span className="text-sm text-[#1A8C7A] font-medium">{item.count}</span>
            </Link>
          ))}
        </div>

        {/* Social Links */}
        <div className={`flex flex-col items-center gap-6 transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-gray-400 text-sm">Suivez-nous sur les réseaux sociaux</p>
          <div className="flex flex-wrap justify-center gap-4">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target={s.href.startsWith("http") ? "_blank" : undefined}
                rel={s.href.startsWith("http") ? "noreferrer" : undefined}
                className="flex items-center gap-3 px-6 py-3 rounded-full border border-white/10 hover:border-white/30 hover:bg-white/5 transition-all duration-200 cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${s.icon} text-lg`} style={{ color: s.color }}></i>
                </div>
                <span className="text-sm text-gray-300">{s.label}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
